/**
 * OffertPosteingangSection — Tab 4a (Offert-Posteingang + IDP; P2 Placement Slice 10). Query =
 * usePlacementOfferten; every mutation goes through the hook and invalidates the server state (offerten
 * + submissions). Kachel/Tabelle toggle (remembered in localStorage), the counters, the Demo-Extraktor
 * chip, the closed/pre-Versand banner, and the upload/zuordnen/normalisieren dialogs.
 */

import { useState } from 'react';
import { CountBadge } from '../../components/ui/CountBadge';
import { Button } from '../../components/ui/Button';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { CarrierLogo } from './CarrierLogo';
import { OffertKachel } from './OffertKachel';
import { OffertTabelle } from './OffertTabelle';
import { OffertUploadDialog } from './OffertUploadDialog';
import { OffertZuordnenDialog } from './OffertZuordnenDialog';
import { OffertNormalisierenDialog } from './OffertNormalisierenDialog';
import {
  OFFER_ANSICHT,
  OFFER_KEINE_OFFERTEN_HINWEIS,
  OFFER_NICHT_ZUGEORDNET_LABEL,
  OFFER_PLACEMENT_STATUS_HINWEIS,
} from '@brokly/shared';
import type { OfferAnsicht, OfferEintrag, OfferNormalisiertBody, OfferSubmissionGruppe } from '@brokly/shared';
import {
  usePlacementOfferten,
  useAktuellOffert,
  useNormalisiertOffert,
  useReExtractOffert,
  useUploadOffert,
  useVerwerfenOffert,
  useZuordnenOffert,
} from './usePlacementOfferten';

const ANSICHT_KEY = 'brokly.offerten.ansicht';

function readAnsicht(): OfferAnsicht {
  try {
    const v = localStorage.getItem(ANSICHT_KEY);
    if (v === OFFER_ANSICHT.TABELLE || v === OFFER_ANSICHT.KACHEL) return v;
  } catch {
    // ignore — a private window / blocked storage falls back to the default.
  }
  return OFFER_ANSICHT.KACHEL;
}

function codeOf(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function GruppeBlock({
  gruppe,
  canWrite,
  aktionErlaubt,
  handlers,
}: {
  gruppe: OfferSubmissionGruppe;
  canWrite: boolean;
  aktionErlaubt: boolean;
  handlers: Record<string, (i: OfferEintrag) => void>;
}) {
  const [showOld, setShowOld] = useState(false);
  const aktuelle = gruppe.aktuelle;
  const rest = gruppe.items.filter((i) => i.id !== aktuelle?.id);
  const unassigned = gruppe.submission_id === null;
  return (
    <div className="flex flex-col gap-2" data-testid={`offert-gruppe-${gruppe.submission_id ?? 'nicht_zugeordnet'}`}>
      <div className="flex items-center gap-2">
        {unassigned ? (
          <span className="text-sm font-semibold">{OFFER_NICHT_ZUGEORDNET_LABEL}</span>
        ) : (
          <>
            <CarrierLogo name={gruppe.name} logoUrl={gruppe.logo_url} size="sm" />
            <span className="text-sm font-semibold">{gruppe.name ?? gruppe.carrier_uid}</span>
            {gruppe.submission_number && <span className="font-mono text-xs text-muted-foreground">{gruppe.submission_number}</span>}
          </>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {aktuelle && (
          <OffertKachel item={aktuelle} gruppe={gruppe} canWrite={canWrite} aktionErlaubt={aktionErlaubt} onOpen={handlers.onOpen!} onZuordnen={handlers.onZuordnen!} onAktuell={handlers.onAktuell!} onReExtract={handlers.onReExtract!} onNormalisieren={handlers.onNormalisieren!} onVerwerfen={handlers.onVerwerfen!} />
        )}
        {(unassigned ? gruppe.items : showOld ? rest : []).map((item) => (
          <OffertKachel key={item.id} item={item} gruppe={gruppe} canWrite={canWrite} aktionErlaubt={aktionErlaubt} onOpen={handlers.onOpen!} onZuordnen={handlers.onZuordnen!} onAktuell={handlers.onAktuell!} onReExtract={handlers.onReExtract!} onNormalisieren={handlers.onNormalisieren!} onVerwerfen={handlers.onVerwerfen!} />
        ))}
      </div>
      {!unassigned && rest.length > 0 && (
        <button type="button" className="self-start text-xs text-brand-navy hover:underline" onClick={() => setShowOld((s) => !s)} data-testid={`offert-aeltere-${gruppe.submission_id}`}>
          {showOld ? 'Aeltere Versionen ausblenden' : `${rest.length} aeltere Version(en) anzeigen`}
        </button>
      )}
    </div>
  );
}

export function OffertPosteingangSection({ placementId, canWrite }: { placementId: string; canWrite: boolean }) {
  const query = usePlacementOfferten(placementId);
  const upload = useUploadOffert(placementId);
  const zuordnen = useZuordnenOffert(placementId);
  const aktuell = useAktuellOffert(placementId);
  const reExtract = useReExtractOffert(placementId);
  const normalisiert = useNormalisiertOffert(placementId);
  const verwerfen = useVerwerfenOffert(placementId);

  const [ansicht, setAnsicht] = useState<OfferAnsicht>(readAnsicht);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [zuordnenItem, setZuordnenItem] = useState<OfferEintrag | null>(null);
  const [normItem, setNormItem] = useState<OfferEintrag | null>(null);
  const [normError, setNormError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  if (query.isLoading) return <p className="text-sm text-muted-foreground">Lade Offerten…</p>;
  const data = query.data;
  if (!data) return null;

  const changeAnsicht = (v: string) => {
    const next = v as OfferAnsicht;
    setAnsicht(next);
    try {
      localStorage.setItem(ANSICHT_KEY, next);
    } catch {
      // ignore
    }
  };

  const run = async (p: Promise<unknown>) => {
    setBanner(null);
    try {
      await p;
    } catch (e) {
      setBanner(codeOf(e));
    }
  };

  const handlers: Record<string, (i: OfferEintrag) => void> = {
    onOpen: () => {},
    onZuordnen: (i) => setZuordnenItem(i),
    onAktuell: (i) => void run(aktuell.mutateAsync({ oid: i.id })),
    onReExtract: (i) => void run(reExtract.mutateAsync({ oid: i.id })),
    onNormalisieren: (i) => {
      setNormError(null);
      setNormItem(i);
    },
    onVerwerfen: (i) => void run(verwerfen.mutateAsync({ oid: i.id, body: {} })),
  };

  const z = data.zaehler;
  const allEmpty = data.gruppen.every((g) => g.items.length === 0);

  return (
    <section className="flex flex-col gap-4" data-testid="placement-offert-posteingang">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold">Offert-Posteingang</h3>
          {data.extractor.is_demo && (
            <span className="rounded-md border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground" data-testid="offert-demo-chip">
              Demo-Extraktor (Fixture)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <SegmentedControl
            value={ansicht}
            onValueChange={changeAnsicht}
            ariaLabel="Ansicht"
            options={[
              { value: OFFER_ANSICHT.KACHEL, label: 'Kacheln' },
              { value: OFFER_ANSICHT.TABELLE, label: 'Tabelle' },
            ]}
          />
          {canWrite && data.aktion_erlaubt && (
            <Button size="sm" onClick={() => { setUploadError(null); setUploadOpen(true); }} data-testid="offert-upload-open">
              Offerte hochladen
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="offert-zaehler">
        <span className="flex items-center gap-1">{z.gesamt} Offerten</span>
        <span className="flex items-center gap-1 text-status-success">{z.aktuell} aktuell</span>
        <span className="flex items-center gap-1 text-status-warning">
          {z.zu_pruefen} zu prüfen {z.zu_pruefen > 0 && <CountBadge count={z.zu_pruefen} />}
        </span>
        <span className="flex items-center gap-1 text-status-info">{z.nicht_zugeordnet} nicht zugeordnet</span>
        {z.fehlgeschlagen > 0 && <span className="flex items-center gap-1 text-status-danger">{z.fehlgeschlagen} fehlgeschlagen</span>}
      </div>

      {!data.aktion_erlaubt && (
        <div className="rounded-md border border-status-warning-border bg-status-warning-bg p-3 text-sm text-status-warning" role="note" data-testid="offert-banner">
          {data.sperrgrund === 'placement_locked'
            ? 'Ausschreibung abgeschlossen - Offerten nur lesend'
            : OFFER_PLACEMENT_STATUS_HINWEIS}
        </div>
      )}

      {banner && (
        <div className="rounded-md border border-status-danger-border bg-status-danger-bg p-3 text-sm text-status-danger" role="alert" data-testid="offert-error-banner">
          {banner}
        </div>
      )}

      {allEmpty ? (
        <p className="text-sm text-muted-foreground" data-testid="offert-empty">{OFFER_KEINE_OFFERTEN_HINWEIS}</p>
      ) : ansicht === OFFER_ANSICHT.TABELLE ? (
        <OffertTabelle gruppen={data.gruppen} canWrite={canWrite} aktionErlaubt={data.aktion_erlaubt} onOpen={handlers.onOpen!} onZuordnen={handlers.onZuordnen!} onAktuell={handlers.onAktuell!} onReExtract={handlers.onReExtract!} onNormalisieren={handlers.onNormalisieren!} onVerwerfen={handlers.onVerwerfen!} />
      ) : (
        <div className="flex flex-col gap-5">
          {data.gruppen.filter((g) => g.items.length > 0).map((g) => (
            <GruppeBlock key={g.submission_id ?? 'nicht_zugeordnet'} gruppe={g} canWrite={canWrite} aktionErlaubt={data.aktion_erlaubt} handlers={handlers} />
          ))}
        </div>
      )}

      <OffertUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        gruppen={data.gruppen}
        uploading={upload.isPending}
        error={uploadError}
        onUpload={async (file, submissionId) => {
          setUploadError(null);
          try {
            await upload.mutateAsync({ file, submissionId });
            setUploadOpen(false);
          } catch (e) {
            setUploadError(codeOf(e));
          }
        }}
      />
      <OffertZuordnenDialog
        open={zuordnenItem !== null}
        onClose={() => setZuordnenItem(null)}
        item={zuordnenItem}
        gruppen={data.gruppen}
        onConfirm={async (submissionId) => {
          const oid = zuordnenItem?.id;
          setZuordnenItem(null);
          if (oid) await run(zuordnen.mutateAsync({ oid, body: { submission_id: submissionId } }));
        }}
      />
      <OffertNormalisierenDialog
        open={normItem !== null}
        onClose={() => setNormItem(null)}
        item={normItem}
        saving={normalisiert.isPending}
        error={normError}
        onSubmit={async (body: OfferNormalisiertBody) => {
          const oid = normItem?.id;
          if (!oid) return;
          setNormError(null);
          try {
            await normalisiert.mutateAsync({ oid, body });
            setNormItem(null);
          } catch (e) {
            setNormError(codeOf(e));
          }
        }}
      />
    </section>
  );
}
